import { isAdmin, isSuperAdmin, isSystemAdmin } from '@utils';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Button,
  ButtonGroup,
  Form,
  OverlayTrigger,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from 'react-bootstrap';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';
import { handleSliderChange, updateImageOpacity } from '../../MapFunctions/MapHelperFunctions';
import { LocationSearch } from './LocationSearch';
import { useMapStore } from '../../MapStore/UseMapStore';
import '../../MapStyles/MapControls.scss';

export function MapControls({ startUpgrade }) {
  const mapID = useMapStore((state) => state.mapID);
  const mapInstance = useMapStore((state) => state.mapInstance);
  const bounds = useMapStore((state) => state.bounds);
  const imageLayer = useMapStore((state) => state.imageLayer);
  const mapLayers = useMapStore((state) => state.mapLayers);
  const setMapLayers = useMapStore((state) => state.setMapLayers);
  const isStatic = useMapStore((state) => state.isStatic);
  const edit = useMapStore((state) => state.edit);
  const reading = useMapStore((state) => state.reading);
  const setReading = useMapStore((state) => state.setReading);
  const upgradeInProg = useMapStore((state) => state.upgradeInProg);
  const submitUpgrade = useMapStore((state) => state.submitUpgrade);
  const markerSize = useMapStore((state) => state.markerSize);
  const setMarkerSize = useMapStore((state) => state.setMarkerSize);
  const opacity = useMapStore((state) => state.opacity);
  const setOpacity = useMapStore((state) => state.setOpacity);

  const [showLayers, setShowLayers] = useState<any>([]);
  const [showTools, setShowTools] = useState<any>([]);
  const [latitude, setLatitude] = useState<number>(null);
  const [longitude, setLongitude] = useState<number>(null);
  const [upgrade, setUpgrade] = useState<boolean>(false);
  const [locConfirm, setLocConfirm] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);

  const history = useHistory();
  const SwalUpgrade = withReactContent(Swal);
  const SwalLocation = withReactContent(Swal);
  const SwalInstructions = withReactContent(Swal);
  const renderTooltip = (tip) => <Tooltip id="button-tooltip">{tip}</Tooltip>;

  useEffect(() => {
    if (upgrade && latitude && longitude) {
      startUpgrade(latitude, longitude);
    }
  }, [upgrade, latitude, longitude]);

  useEffect(() => {
    if (SwalLocation.getConfirmButton())
      SwalLocation.getConfirmButton().removeAttribute('disabled');
  }, [locConfirm]);

  const handleSubmitAlignment = async () => {
    if (submitting) return;
    setSubmitting(true);

    // Wait for the server to finish converting (and translating map objects)
    // before leaving edit mode, so the refetch gets the new positions
    const success = await submitUpgrade();

    setSubmitting(false);
    if (success) {
      setUpgrade(false);
      history.push('/maps/' + mapID);
    }
  };

  const showGeoPopup = () => {
    SwalUpgrade.fire({
      allowOutsideClick: false,
      title: `Geospatial Map`,
      html: '<Text>Do you want to begin the one time upgrade process to transform this Map into a Geospatial Map associated with a real-world location?<br/><br/><a href="https://thinaer.io/help-center/maps/beta-maps-geospatial-versus-static/" target="_blank">Geospatial Maps versus Static Maps</a><Text/>',
      icon: 'warning',
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Continue',
    }).then((submit) => {
      if (submit.isConfirmed) {
        showLocationPopup();
      }
    });
  };

  const showLocationPopup = () => {
    SwalLocation.fire({
      allowOutsideClick: false,
      title: `Geospatial Map Location`,
      html: (
        <LocationSearch
          setLatitude={(e) => setLatitude(e)}
          setLongitude={(e) => setLongitude(e)}
          setLocConfirm={(e) => setLocConfirm(e)}
        />
      ),
      showCancelButton: true,
      cancelButtonText: 'Cancel',
      confirmButtonText: 'Submit',
      didOpen: () => {
        SwalLocation.getConfirmButton().setAttribute('disabled', 'disabled');
      },
    }).then((submit) => {
      if (submit.isConfirmed) {
        showInstructionsPopup();
      }
    });
  };

  const showInstructionsPopup = () => {
    SwalInstructions.fire({
      allowOutsideClick: false,
      title: `Last Step, Finish Alignment!`,
      html: '<Text>Almost there - use your mouse to finish aligning the Map. Please ensure at least 3 corners are properly aligned for accurate translation.<br/><br/><a href="https://thinaer.io/help-center/beta-maps/beta-maps-aligning-map-how-to/" target="_blank">Aligning Map Guide</a><Text/>',
      icon: 'success',
      confirmButtonText: 'Okay',
    }).then((submit) => {
      if (submit.isConfirmed) {
        setUpgrade(true);
      }
    });
  };

  return (
    <>
      <div className="bottom-container">
        <div className="d-flex flex-row justify-content-between align-items-center">
          <ToggleButtonGroup
            className="d-flex flex-row align-items-center justify-content-between me-3"
            type="checkbox"
            value={showLayers}
            onChange={(val) => {
              setShowLayers(val);
            }}
          >
            <ToggleButton
              className="d-flex flex-row align-items-center justify-content-between map-layer-menu"
              id={'Layer Menu'}
              variant={showLayers.includes('true') ? 'primary' : 'secondary'}
              value={'true'}
              data-tooltip-id="tooltip"
              data-tooltip-content="Layer Menu"
            >
              <i className="sonarIcon sonarIcon-stack fs-md" />
            </ToggleButton>
          </ToggleButtonGroup>
          {showLayers.includes('true') && (
            <>
              <ToggleButtonGroup
                className="d-flex flex-row align-items-center justify-content-between me-3"
                type="checkbox"
                value={mapLayers}
                onChange={(val) => {
                  setMapLayers(val);
                }}
              >
                <ToggleButton
                  className="d-flex flex-row align-items-center justify-content-between"
                  id={'edge'}
                  variant={mapLayers.includes('edge') ? 'primary' : 'secondary'}
                  value={'edge'}
                  data-tooltip-id="tooltip"
                  data-tooltip-content="Toggle Map Edge"
                >
                  <i className="sonarIcon sonarIcon-border_style fs-md" />
                </ToggleButton>

                {!isStatic && (
                  <ToggleButton
                    className="d-flex flex-row align-items-center justify-content-between"
                    id={'sat'}
                    variant={mapLayers.includes('sat') ? 'primary' : 'secondary'}
                    value={'sat'}
                    data-tooltip-id="tooltip"
                    data-tooltip-content="Toggle Satellite"
                  >
                    <i className="sonarIcon sonarIcon-satellite fs-md" />
                  </ToggleButton>
                )}
                {!edit && (
                  <ToggleButton
                    className="d-flex flex-row align-items-center justify-content-between"
                    id={'asset'}
                    variant={mapLayers.includes('asset') ? 'primary' : 'secondary'}
                    value={'asset'}
                    data-tooltip-id="tooltip"
                    data-tooltip-content="Toggle Assets"
                  >
                    <i className="sonarIcon sonarIcon-asset fs-md" />
                  </ToggleButton>
                )}
                <ToggleButton
                  className="d-flex flex-row align-items-center justify-content-between"
                  id={'place'}
                  variant={mapLayers.includes('place') ? 'primary' : 'secondary'}
                  value={'place'}
                  data-tooltip-id="tooltip"
                  data-tooltip-content="Toggle Places"
                >
                  <i className="sonarIcon sonarIcon-place fs-md" />
                </ToggleButton>
                <ToggleButton
                  className="d-flex flex-row align-items-center justify-content-between"
                  id={'gateway'}
                  variant={mapLayers.includes('gateway') ? 'primary' : 'secondary'}
                  value={'gateway'}
                  data-tooltip-id="tooltip"
                  data-tooltip-content="Toggle Gateways"
                >
                  <i className="sonarIcon sonarIcon-gateway fs-md" />
                </ToggleButton>
                <ToggleButton
                  className="d-flex flex-row align-items-center justify-content-between"
                  id={'stationary'}
                  variant={mapLayers.includes('stationary') ? 'primary' : 'secondary'}
                  value={'stationary'}
                  data-tooltip-id="tooltip"
                  data-tooltip-content="Toggle Stationary Assets"
                >
                  <i className="sonarIcon sonarIcon-stationary-beacon fs-md" />
                </ToggleButton>
              </ToggleButtonGroup>
              {mapLayers.includes('stationary asset') && (
                <ToggleButtonGroup
                  className="d-flex flex-row align-items-center justify-content-between me-3"
                  type="radio"
                  value={reading}
                  name="readings"
                  onChange={(val) => setReading(val)}
                >
                  <ToggleButton
                    className="d-flex flex-row align-items-center justify-content-between"
                    id={'Temperature'}
                    variant={reading === 'Temperature' ? 'primary' : 'secondary'}
                    value="Temperature"
                    data-tooltip-id="tooltip"
                    data-tooltip-content="Toggle Temperature"
                  >
                    <i className="sonarIcon sonarIcon-temperature fs-md" />
                  </ToggleButton>
                  <ToggleButton
                    className="d-flex flex-row align-items-center justify-content-between"
                    id={'Humidity'}
                    variant={reading === 'Humidity' ? 'primary' : 'secondary'}
                    value="Humidity"
                    data-tooltip-id="tooltip"
                    data-tooltip-content="Toggle Humidity"
                  >
                    <i className="sonarIcon sonarIcon-humidity fs-md" />
                  </ToggleButton>
                  <ToggleButton
                    className="d-flex flex-row align-items-center justify-content-between"
                    id={'Vibration'}
                    variant={reading === 'Vibration' ? 'primary' : 'secondary'}
                    value="Vibration"
                    data-tooltip-id="tooltip"
                    data-tooltip-content="Toggle Vibration"
                  >
                    <i className="sonarIcon sonarIcon-vibration fs-md" />
                  </ToggleButton>
                  <ToggleButton
                    className="d-flex flex-row align-items-center justify-content-between"
                    id={'Battery'}
                    variant={reading === 'Battery' ? 'primary' : 'secondary'}
                    value="Battery"
                    data-tooltip-id="tooltip"
                    data-tooltip-content="Toggle Battery"
                  >
                    <i className="sonarIcon sonarIcon-battery fs-md" />
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            </>
          )}
        </div>
        {!upgradeInProg && isStatic && edit && (isAdmin || isSystemAdmin || isSuperAdmin) && (
          <OverlayTrigger
            delay={{ show: 500, hide: 250 }}
            overlay={renderTooltip(
              'Click to associate this map with a real-world location. This action cannot be reversed',
            )}
          >
            <Button
              variant="secondary"
              onClick={() => {
                showGeoPopup();
              }}
            >
              Upgrade to Geospatial
            </Button>
          </OverlayTrigger>
        )}
        {upgradeInProg && (
          <div className="d-flex flex-row justify-content-between">
            <Button
              className="me-3"
              variant="primary"
              disabled={submitting}
              onClick={handleSubmitAlignment}
            >
              Submit Alignment
            </Button>
            <Button
              variant="secondary"
              disabled={submitting}
              onClick={() => {
                window.location.reload();
              }}
            >
              Cancel
            </Button>
          </div>
        )}

        <div className="d-flex flex-row justify-content-between align-items-center">
          {showTools.includes('true') && (
            <>
              <div className="opacity-slider">
                <Form.Label className="slider-label">Overlay Opacity</Form.Label>
                <Form.Range
                  className="custom-range"
                  title="Map Opacity"
                  value={opacity.toString()}
                  onChange={(e) => {
                    updateImageOpacity(e.target.value, imageLayer);
                    setOpacity(parseInt(e.target.value));
                  }}
                />
              </div>
              <div className="opacity-slider">
                <Form.Label className="slider-label">Marker Size</Form.Label>
                <Form.Range
                  className="custom-range"
                  title="Marker Size"
                  min={30}
                  max={60}
                  value={markerSize.toString()}
                  onChange={(e) => {
                    handleSliderChange(e as any);
                    setMarkerSize(parseInt(e.target.value));
                  }}
                />
              </div>
              <ButtonGroup className="map-controls me-3">
                <Button
                  title="Center Map"
                  className="d-flex flex-row align-items-center justify-content-between"
                  variant="secondary"
                  data-tooltip-id="tooltip"
                  data-tooltip-content="Center Map"
                  onClick={() => {
                    mapInstance.fitBounds(bounds);
                    mapInstance.invalidateSize(true);
                  }}
                >
                  <i className="sonarIcon sonarIcon-center fs-md" />
                </Button>
              </ButtonGroup>
            </>
          )}
          <ToggleButtonGroup
            className="d-flex flex-row align-items-center justify-content-between"
            type="checkbox"
            value={showTools}
            onChange={(val) => {
              setShowTools(val);
            }}
          >
            <ToggleButton
              className="d-flex flex-row align-items-center justify-content-between map-tools-menu"
              id={'Tools Menu'}
              variant={showTools.includes('true') ? 'primary' : 'secondary'}
              value={'true'}
              data-tooltip-id="tooltip"
              data-tooltip-content="Tools Menu"
            >
              <i className="sonarIcon sonarIcon-tools fs-md" />
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      </div>
      <ReactTooltip id="tooltip" place="top" delayShow={300} style={{ zIndex: 9999 }} />
    </>
  );
}
